import { logout } from "@/redux/slices/user.slice";
import {
  fetchAutoregistry,
  getAutoregistryMonthMap,
  fetchAutoregistryTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  fetchScheduledAutoregisters,
  createSchedule,
  updateSchedule,
  deleteSchedule,
} from "@/redux/thunks/autoregistry.thunk";
import { createSlice } from "@reduxjs/toolkit";

interface AutoregistryState {
  data: any | null;
  loading: boolean;
  success: boolean;
  error: any | boolean;
  calendarStatus: "loading" | "success" | "error" | "idle";
  calendarData: any | null;

  templates: any[];
  templatesLoading: boolean;
  templatesError: any | null;
  templateActionStatus: "idle" | "loading" | "success" | "error";
  templateActionError: any | null;

  schedules: any[];
  schedulesLoading: boolean;
  schedulesError: any | null;
  scheduleActionStatus: "idle" | "loading" | "success" | "error";
  scheduleActionError: any | null;
}

const initialState: AutoregistryState = {
  data: [],
  loading: false,
  success: true,
  error: false,
  calendarData: [],
  calendarStatus: "idle",

  templates: [],
  templatesLoading: false,
  templatesError: null,
  templateActionStatus: "idle",
  templateActionError: null,

  schedules: [],
  schedulesLoading: false,
  schedulesError: null,
  scheduleActionStatus: "idle",
  scheduleActionError: null,
};

const autoregistrySlice = createSlice({
  name: "autoregistry",
  initialState,
  reducers: {
    clearAutoregistry: (state) => {
      Object.assign(state, initialState);
    },
    resetTemplateActionStatus: (state) => {
      state.templateActionStatus = "idle";
      state.templateActionError = null;
    },
    resetScheduleActionStatus: (state) => {
      state.scheduleActionStatus = "idle";
      state.scheduleActionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAutoregistry.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = false;
      })
      .addCase(fetchAutoregistry.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(fetchAutoregistry.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.error;
      });
    builder
      .addCase(logout, (state) => {
        Object.assign(state, initialState);
      })
      .addCase(getAutoregistryMonthMap.pending, (state) => {
        state.calendarStatus = "loading";
        state.calendarData = [];
      })
      .addCase(getAutoregistryMonthMap.fulfilled, (state, action) => {
        state.calendarStatus = "success";
        state.calendarData = action.payload;
      })
      .addCase(getAutoregistryMonthMap.rejected, (state, action) => {
        state.calendarStatus = "error";
        state.calendarData = [];
      });

    builder
      .addCase(fetchAutoregistryTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchAutoregistryTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;

        state.templates = action.payload || [];
      })
      .addCase(fetchAutoregistryTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.error;
      });

    builder
      .addCase(createTemplate.pending, (state) => {
        state.templateActionStatus = "loading";
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.templateActionStatus = "success";

        state.templates = [...state.templates, action.payload.data];
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.templateActionStatus = "error";
        state.templateActionError = action.error;
      });

    builder
      .addCase(updateTemplate.pending, (state) => {
        state.templateActionStatus = "loading";
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.templateActionStatus = "success";
        const updatedTemplate = action.payload.data;
        state.templates = state.templates.map((template) =>
          template.id === updatedTemplate.id ? updatedTemplate : template
        );
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.templateActionStatus = "error";
        state.templateActionError = action.error;
      });

    builder
      .addCase(deleteTemplate.pending, (state) => {
        state.templateActionStatus = "loading";
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.templateActionStatus = "success";
        const deletedId = action.meta.arg;
        state.templates = state.templates.filter(
          (template) => template.id !== deletedId
        );
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.templateActionStatus = "error";
        state.templateActionError = action.error;
      });

    // template
    builder
      .addCase(fetchScheduledAutoregisters.pending, (state) => {
        state.schedulesLoading = true;
        state.schedulesError = null;
      })
      .addCase(fetchScheduledAutoregisters.fulfilled, (state, action) => {
        state.schedulesLoading = false;
        state.schedules = action.payload || [];
      })
      .addCase(fetchScheduledAutoregisters.rejected, (state, action) => {
        state.schedulesLoading = false;
        state.schedulesError = action.error;
      });

    builder
      .addCase(createSchedule.pending, (state) => {
        state.scheduleActionStatus = "loading";
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        state.scheduleActionStatus = "success";
        state.schedules = [action.payload.data, ...state.schedules];
      })
      .addCase(createSchedule.rejected, (state, action) => {
        state.scheduleActionStatus = "error";
        state.scheduleActionError = action.error;
      });

    builder
      .addCase(updateSchedule.pending, (state) => {
        state.scheduleActionStatus = "loading";
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        state.scheduleActionStatus = "success";
        const updatedSchedule = action.payload;

        state.schedules = state.schedules.map((schedule) =>
          schedule.id === updatedSchedule.id ? updatedSchedule : schedule
        );
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        state.scheduleActionStatus = "error";
        state.scheduleActionError = action.error;
      });

    builder
      .addCase(deleteSchedule.pending, (state) => {
        state.scheduleActionStatus = "loading";
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        state.scheduleActionStatus = "success";
        const deletedId = action.meta.arg;
        state.schedules = state.schedules.filter(
          (schedule) => schedule.id !== deletedId
        );
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.scheduleActionStatus = "error";
        state.scheduleActionError = action.error;
      });
  },
});

export const {
  clearAutoregistry,
  resetTemplateActionStatus,
  resetScheduleActionStatus,
} = autoregistrySlice.actions;

export default autoregistrySlice.reducer;
